import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import "../../../../assets/css/patient/patientlayouts/layoutpages/MySettings.css";
import axios from "axios";
import { AppContext } from "../../../../context/AppContext";

const emptyProfile = {
  fullName: "",
  email: "",
  phone: "",
  dateOfBirth: "",
  gender: "",
  city: "",
  country: "",
  language: "",
};

const defaultNotifications = {
  emailNotifications: true,
  smsReminders: true,
  appointmentReminders: true,
  newsletter: false,
};

/* Inline SVG tab icons — plain outline style, rendered with
   stroke="currentColor" so they inherit the button's text color
   (grey when idle, white when active). Using real SVG instead of
   emoji avoids the "blank tab" bug: some mobile browsers/webviews
   don't ship emoji glyphs and silently render nothing. */
const ProfileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const BellIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const MySettings = () => {
  const { baseUrl, user } = useContext(AppContext);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("profile");

  const [profile, setProfile] = useState(emptyProfile);
  const [savedProfile, setSavedProfile] = useState(emptyProfile);
  const [isEditing, setIsEditing] = useState(true);
  const [saving, setSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [twoFactor, setTwoFactor] = useState(false);
  const [loginAlerts, setLoginAlerts] = useState(true);

  const [notifications, setNotifications] = useState(defaultNotifications);

  const [feedback, setFeedback] = useState({ text: "", type: "" });

  const userId = localStorage.getItem("userId");

  // Auth header helper — every /Patient/PatientAuth/* route sits behind
  // the auth:patient middleware, so each call below must send this.
  const authHeaders = () => {
    const token = localStorage.getItem("patient_token");
    return { Authorization: `Bearer ${token}`, Accept: "application/json" };
  };

  const tabs = [
    { id: "profile", label: "Profile", icon: <ProfileIcon /> },
    { id: "security", label: "Security", icon: <LockIcon /> },
    { id: "notifications", label: "Notifications", icon: <BellIcon /> },
  ];

  const showFeedback = (text, type) => {
    setFeedback({ text, type });
    setTimeout(() => setFeedback({ text: "", type: "" }), 4000);
  };

  // AUTO-FILL name/email from login session
  useEffect(() => {
    if (user) {
      setProfile((previousProfile) => ({
        ...previousProfile,
        fullName: previousProfile.fullName || user.name || "",
        email: previousProfile.email || user.email || "",
      }));
    }
  }, [user]);

  // LOAD LOCAL SETTINGS (initial, before server responds)
  useEffect(() => {
    try {
      const storedProfile = localStorage.getItem("patient_settings_profile");
      if (storedProfile) {
        const parsedProfile = { ...emptyProfile, ...JSON.parse(storedProfile) };
        setProfile(parsedProfile);
        setSavedProfile(parsedProfile);
        setIsEditing(false);
      }

      const storedSecurity = localStorage.getItem("patient_settings_security");
      if (storedSecurity) {
        const security = JSON.parse(storedSecurity);
        setTwoFactor(!!security.twoFactor);
        setLoginAlerts(security.loginAlerts !== false);
      }

      const storedNotifications = localStorage.getItem("patient_settings_notifications");
      if (storedNotifications) {
        setNotifications({ ...defaultNotifications, ...JSON.parse(storedNotifications) });
      }
    } catch (error) {
      console.error("Error loading saved settings:", error);
    }
  }, []);

  // FETCH PATIENT PROFILE FROM SERVER
  useEffect(() => {
    if (!userId) {
      console.log("Patient userId not found.");
      return;
    }

    axios
      .get(`${baseUrl}/Patient/PatientAuth/getProfile/${userId}`, {
        headers: authHeaders(),
      })
      .then((response) => {
        const data = response.data?.data;
        if (data) {
          const profileData = {
            fullName: data.full_name || "",
            email: data.email || "",
            phone: data.phone || "",
            dateOfBirth: data.date_of_birth || "",
            gender: data.gender || "",
            city: data.city || "",
            country: data.country || "",
            language: data.language || "",
          };
          setProfile(profileData);
          setSavedProfile(profileData);
          localStorage.setItem("patient_settings_profile", JSON.stringify(profileData));
          setIsEditing(false);
        }
      })
      .catch((error) => console.error("Get profile error:", error));
  }, [baseUrl, userId]);

  // FETCH NOTIFICATION SETTINGS FROM SERVER
  useEffect(() => {
    if (!userId) return;

    axios
      .get(`${baseUrl}/Patient/PatientAuth/getSettings/${userId}`, {
        headers: authHeaders(),
      })
      .then((response) => {
        const data = response.data?.data;
        if (data) {
          setNotifications({
            emailNotifications: !!data.email_notifications,
            smsReminders: !!data.sms_reminders,
            appointmentReminders: !!data.appointment_reminders,
            newsletter: !!data.newsletter,
          });
        }
      })
      .catch((error) => console.error("Get settings error:", error));
  }, [baseUrl, userId]);

  const handleFieldChange = (field, value) => {
    setProfile((previousProfile) => ({ ...previousProfile, [field]: value }));
  };

  const handleEditClick = () => setIsEditing(true);

  const handleCancelClick = () => {
    setProfile(savedProfile);
    setIsEditing(false);
  };

  const validateProfile = () => {
    if (!profile.fullName.trim()) {
      showFeedback("Full Name is required", "error");
      return false;
    }
    if (!profile.email.trim()) {
      showFeedback("Email is required", "error");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profile.email)) {
      showFeedback("Please enter a valid email address", "error");
      return false;
    }
    if (!profile.phone.trim()) {
      showFeedback("Phone is required", "error");
      return false;
    }
    return true;
  };

  const updateProfile = async () => {
    if (!userId) {
      showFeedback("Patient ID not found. Please login again.", "error");
      return;
    }
    if (!validateProfile()) return;

    setSaving(true);
    try {
      const response = await axios.post(
        `${baseUrl}/Patient/PatientAuth/updateProfile`,
        {
          userId,
          fullName: profile.fullName,
          email: profile.email,
          phone: profile.phone,
          dateOfBirth: profile.dateOfBirth,
          gender: profile.gender,
          city: profile.city,
          country: profile.country,
          language: profile.language,
        },
        { headers: authHeaders() }
      );

      console.log("Update profile response:", response.data);

      setSavedProfile(profile);
      localStorage.setItem("patient_settings_profile", JSON.stringify(profile));
      setIsEditing(false);
      showFeedback("Profile saved successfully", "success");
      navigate("/patientdashboard/profile");
    } catch (error) {
      console.error("Update profile error:", error);
      showFeedback(error?.response?.data?.message || "Could not update profile", "error");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      showFeedback("Please fill all password fields", "error");
      return;
    }
    if (newPassword !== confirmPassword) {
      showFeedback("New password and confirmation do not match", "error");
      return;
    }
    if (!userId) {
      showFeedback("Patient ID not found. Please login again.", "error");
      return;
    }

    try {
      const response = await axios.post(
        `${baseUrl}/Patient/PatientAuth/changePassword`,
        {
          userId,
          currentPassword,
          newPassword,
        },
        { headers: authHeaders() }
      );

      console.log("Password update response:", response.data);
      showFeedback("Password updated successfully", "success");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Password update error:", error);
      showFeedback(error?.response?.data?.message || "Could not update password", "error");
    }
  };

  const toggleSecurityOption = (key, value) => {
    const next = {
      twoFactor: key === "twoFactor" ? value : twoFactor,
      loginAlerts: key === "loginAlerts" ? value : loginAlerts,
    };
    setTwoFactor(next.twoFactor);
    setLoginAlerts(next.loginAlerts);
    localStorage.setItem("patient_settings_security", JSON.stringify(next));
    showFeedback("Security setting updated", "success");
  };

  // NOTIFICATION TOGGLE — backend ko bhi save karta hai
  const toggleNotification = (key) => {
    const next = { ...notifications, [key]: !notifications[key] };
    setNotifications(next);
    localStorage.setItem("patient_settings_notifications", JSON.stringify(next));

    if (userId) {
      axios
        .post(
          `${baseUrl}/Patient/PatientAuth/updateSettings`,
          {
            userId,
            emailNotifications: next.emailNotifications,
            smsReminders: next.smsReminders,
            appointmentReminders: next.appointmentReminders,
            newsletter: next.newsletter,
          },
          { headers: authHeaders() }
        )
        .then(() => showFeedback("Notification preference updated", "success"))
        .catch(() => showFeedback("Could not save preference", "error"));
    }
  };

  const handleDeactivate = () => {
    const confirmed = window.confirm(
      "Are you sure you want to deactivate your account? You can reactivate anytime by logging back in."
    );
    if (confirmed) showFeedback("Account deactivation requested.", "success");
  };

  const handleDeleteAccount = () => {
    const confirmed = window.confirm(
      "This will permanently delete your account and all data. This cannot be undone. Continue?"
    );
    if (confirmed) showFeedback("Account deletion requested.", "error");
  };

  return (
    <div className="pms-settings-container">
      <div className="pms-settings-header">
        <h2>Settings</h2>
        <p>Manage your account settings and preferences</p>
      </div>

      {feedback.text && (
        <div className={feedback.type === "error" ? "pms-feedback pms-feedback-error" : "pms-feedback pms-feedback-success"}>
          {feedback.text}
        </div>
      )}

      <div className="pms-settings-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`pms-tab-btn ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="pms-tab-content">
        {activeTab === "profile" && (
          <div className="pms-settings-section">
            <div className="pms-section-header">
              <div>
                <h3>Profile Information</h3>
                <p>Your personal and contact details</p>
              </div>
            </div>

            <div className="pms-form-content">
              <div className="pms-form-row">
                <div className="pms-form-group">
                  <label>Full Name</label>
                  <input type="text" value={profile.fullName} onChange={(e) => handleFieldChange("fullName", e.target.value)} disabled={!isEditing} placeholder="Enter your full name" />
                </div>

                <div className="pms-form-group">
                  <label>Email</label>
                  <input type="email" value={profile.email} onChange={(e) => handleFieldChange("email", e.target.value)} disabled={!isEditing} placeholder="Enter your email" />
                </div>

                <div className="pms-form-group">
                  <label>Phone</label>
                  <input type="tel" value={profile.phone} onChange={(e) => handleFieldChange("phone", e.target.value)} disabled={!isEditing} placeholder="Enter your phone number" />
                </div>

                <div className="pms-form-group">
                  <label>Date of Birth</label>
                  <input type="date" value={profile.dateOfBirth} onChange={(e) => handleFieldChange("dateOfBirth", e.target.value)} disabled={!isEditing} />
                </div>

                <div className="pms-form-group">
                  <label>Gender</label>
                  <select value={profile.gender} onChange={(e) => handleFieldChange("gender", e.target.value)} disabled={!isEditing}>
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="pms-form-group">
                  <label>City</label>
                  <input type="text" value={profile.city} onChange={(e) => handleFieldChange("city", e.target.value)} disabled={!isEditing} placeholder="Enter your city" />
                </div>

                <div className="pms-form-group">
                  <label>Country</label>
                  <input type="text" value={profile.country} onChange={(e) => handleFieldChange("country", e.target.value)} disabled={!isEditing} placeholder="Enter your country" />
                </div>

                <div className="pms-form-group">
                  <label>Preferred Language</label>
                  <input type="text" value={profile.language} onChange={(e) => handleFieldChange("language", e.target.value)} disabled={!isEditing} placeholder="e.g. Urdu, English" />
                </div>
              </div>

              <div className="pms-action-buttons">
                {isEditing ? (
                  <>
                    <button className="pms-btn-save" onClick={updateProfile} disabled={saving}>
                      {saving ? "Saving..." : "Save Profile"}
                    </button>
                    {savedProfile.fullName && (
                      <button className="pms-btn-delete" type="button" onClick={handleCancelClick} style={{ background: "#6b7280" }}>
                        Cancel
                      </button>
                    )}
                  </>
                ) : (
                  <button className="pms-btn-save" onClick={handleEditClick}>
                    Edit Profile
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "security" && (
          <div className="pms-settings-section">
            <div className="pms-section-header">
              <div>
                <h3>Password & Security</h3>
                <p>Keep your account safe and up to date</p>
              </div>
            </div>

            <div className="pms-security-card">
              <h4>Change Password</h4>
              <div className="pms-form-content">
                <div className="pms-form-row">
                  <div className="pms-form-group" style={{ gridColumn: "1 / -1" }}>
                    <label>Current Password</label>
                    <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password" />
                  </div>
                  <div className="pms-form-group">
                    <label>New Password</label>
                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" />
                  </div>
                  <div className="pms-form-group">
                    <label>Confirm Password</label>
                    <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" />
                  </div>
                </div>
                <div className="pms-action-buttons">
                  <button className="pms-btn-save" onClick={handlePasswordUpdate}>Update Password</button>
                </div>
              </div>
            </div>

            <div className="pms-security-card">
              <h4>Login Security</h4>
              <div className="pms-toggle-setting">
                <div className="pms-toggle-info">
                  <strong>Two-Factor Authentication</strong>
                  <p>Add an extra layer of security with a verification code at login.</p>
                </div>
                <label className="pms-toggle-switch">
                  <input type="checkbox" checked={twoFactor} onChange={(e) => toggleSecurityOption("twoFactor", e.target.checked)} />
                  <span className="pms-slider" />
                </label>
              </div>

              <div className="pms-toggle-setting">
                <div className="pms-toggle-info">
                  <strong>Login Alerts</strong>
                  <p>Get notified by email whenever a new device logs into your account.</p>
                </div>
                <label className="pms-toggle-switch">
                  <input type="checkbox" checked={loginAlerts} onChange={(e) => toggleSecurityOption("loginAlerts", e.target.checked)} />
                  <span className="pms-slider" />
                </label>
              </div>
            </div>

            <div className="pms-danger-zone">
              <h4>Danger Zone</h4>
              <p>Deactivating pauses your account temporarily; deleting removes it permanently and cannot be undone.</p>
              <div className="pms-action-buttons">
                <button className="pms-btn-delete" onClick={handleDeactivate}>Deactivate Account</button>
                <button className="pms-btn-delete" onClick={handleDeleteAccount}>Delete Account</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "notifications" && (
          <div className="pms-settings-section">
            <div className="pms-section-header">
              <div>
                <h3>Notification Preferences</h3>
                <p>Choose how you'd like to hear from us</p>
              </div>
            </div>

            <div className="pms-notification-group">
              <h4>Notifications</h4>

              <div className="pms-toggle-setting">
                <div className="pms-toggle-info">
                  <strong>Email Notifications</strong>
                  <p>Receive updates about your account and sessions by email.</p>
                </div>
                <label className="pms-toggle-switch">
                  <input type="checkbox" checked={notifications.emailNotifications} onChange={() => toggleNotification("emailNotifications")} />
                  <span className="pms-slider" />
                </label>
              </div>

              <div className="pms-toggle-setting">
                <div className="pms-toggle-info">
                  <strong>SMS Reminders</strong>
                  <p>Get text message reminders for upcoming sessions.</p>
                </div>
                <label className="pms-toggle-switch">
                  <input type="checkbox" checked={notifications.smsReminders} onChange={() => toggleNotification("smsReminders")} />
                  <span className="pms-slider" />
                </label>
              </div>

              <div className="pms-toggle-setting">
                <div className="pms-toggle-info">
                  <strong>Appointment Reminders</strong>
                  <p>In-app reminders 24 hours before your appointment.</p>
                </div>
                <label className="pms-toggle-switch">
                  <input type="checkbox" checked={notifications.appointmentReminders} onChange={() => toggleNotification("appointmentReminders")} />
                  <span className="pms-slider" />
                </label>
              </div>

              <div className="pms-toggle-setting">
                <div className="pms-toggle-info">
                  <strong>Newsletter</strong>
                  <p>Occasional tips and updates from the platform.</p>
                </div>
                <label className="pms-toggle-switch">
                  <input type="checkbox" checked={notifications.newsletter} onChange={() => toggleNotification("newsletter")} />
                  <span className="pms-slider" />
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MySettings;